import type { ReactNode } from "react";
import { SvgIcon } from "./ui/SvgIcon";
import checkIcon from "../assets/check-icon.svg?raw";
import { getThemeColors } from "../lib/theme";

interface ArticleActionButtonProps {
  variant?: "primary" | "selected" | "muted" | "link";
  onClick?: () => void;
  disabled?: boolean;
  isDark: boolean;
  testId?: string;
  children: ReactNode;
}

const variantClasses = {
  primary: (isDark: boolean) => getThemeColors(isDark).buttonSmall,
  // Already picked: a flat mint piece with a check, not pressable.
  selected: () =>
    "inline-flex items-center gap-1.5 rounded-lg border-2 border-ink bg-mint px-3 py-1.5 font-display text-xs text-ink cursor-default",
  muted: (isDark: boolean) =>
    `${getThemeColors(isDark).buttonSmall} opacity-80 hover:opacity-100`,
  // Inherits the card's text color so it reads on board pieces too.
  link: () =>
    "text-sm font-bold cursor-pointer transition-opacity text-current opacity-70 hover:opacity-100 underline-offset-4 hover:underline",
} as const;

export function ArticleActionButton({
  variant = "primary",
  onClick,
  disabled = false,
  isDark,
  testId,
  children,
}: ArticleActionButtonProps) {
  const isSelected = variant === "selected";

  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || isSelected}
      className={variantClasses[variant](isDark)}
    >
      <span className={isSelected ? "contents" : "hidden"} aria-hidden="true">
        <SvgIcon markup={checkIcon} size={12} strokeWidth={3} />
      </span>
      {children}
    </button>
  );
}
